import jsPDF from 'jspdf';
import { ProductDimensions, MaterialType, PackagingResult } from './packagingCalculator';

export const generatePackagingPDF = (
  product: ProductDimensions,
  material: MaterialType,
  result: PackagingResult
): jsPDF => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  
  // Funkcja do bezpiecznego wyświetlania tekstu z polskimi znakami
  const safeText = (text: string): string => {
    if (!text) return '';
    return text
      .replace(/ł/g, 'l')
      .replace(/Ł/g, 'L')
      .replace(/ą/g, 'a')
      .replace(/ę/g, 'e')
      .replace(/ś/g, 's')
      .replace(/ć/g, 'c')
      .replace(/ń/g, 'n')
      .replace(/ż/g, 'z')
      .replace(/ź/g, 'z')
      .replace(/ó/g, 'o')
      .replace(/Ą/g, 'A')
      .replace(/Ę/g, 'E')
      .replace(/Ś/g, 'S')
      .replace(/Ć/g, 'C')
      .replace(/Ń/g, 'N')
      .replace(/Ż/g, 'Z')
      .replace(/Ź/g, 'Z')
      .replace(/Ó/g, 'O');
  };
  
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  
  // ============= STRONA 1: KARTA KONFIGURACJI =============
  
  // Nagłówek firmowy
  pdf.setFontSize(20);
  pdf.setTextColor(255, 102, 0); // Pomarańczowy
  pdf.text('PlexiSystem S.C.', 20, 20);
  
  pdf.setFontSize(10);
  pdf.setTextColor(100, 100, 100);
  pdf.text(safeText('Ks. Dr. Leona Heyke 11'), 20, 27);
  pdf.text(safeText('84-206 Nowy Dwor Wejherowski'), 20, 32);
  pdf.text('NIP: 588-239-62-72', 20, 37);
  pdf.text('Tel: 884 042 107', 20, 42);
  pdf.text('www.plexisystem.pl', 20, 47);
  
  // Logo/ikona po prawej stronie
  pdf.setFillColor(255, 102, 0);
  pdf.rect(pageWidth - 50, 15, 30, 30, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(24);
  pdf.text('PKG', pageWidth - 35, 35, { align: 'center' });
  
  // Tytuł dokumentu
  pdf.setFontSize(18);
  pdf.setTextColor(0, 0, 0);
  pdf.text(safeText('INSTRUKCJA PAKOWANIA'), pageWidth / 2, 60, { align: 'center' });
  
  // Numer dokumentu i data
  pdf.setFontSize(10);
  pdf.setTextColor(100, 100, 100);
  const docNumber = `#IP${new Date().getTime().toString().slice(-8)}`;
  pdf.text(safeText(`Dokument: ${docNumber}`), pageWidth / 2, 67, { align: 'center' });
  pdf.text(safeText(`Data: ${new Date().toLocaleDateString('pl-PL')}`), pageWidth / 2, 72, { align: 'center' });
  
  // Sekcja: Parametry produktu
  let yPos = 85;
  
  // Nagłówek sekcji z tłem
  pdf.setFillColor(240, 240, 240);
  pdf.rect(20, yPos, pageWidth - 40, 8, 'F');
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  pdf.text('PRODUKT', 22, yPos + 5.5);
  pdf.setFont('helvetica', 'normal');
  
  yPos += 12;
  
  // Parametry produktu w formie tabeli
  pdf.setFontSize(10);
  const productData = [
    ['Material:', material.name],
    ['Wymiary:', `${product.length} x ${product.width} x ${product.height} mm`],
    ['Waga jednostkowa:', `${product.weight} kg`],
    ['Ilosc sztuk:', `${product.quantity} szt.`],
    ['Krucho:', 
      material.fragility === 'very_high' ? 'Bardzo wysoka' :
      material.fragility === 'high' ? 'Wysoka' :
      material.fragility === 'medium' ? 'Srednia' : 'Niska'
    ]
  ];
  
  productData.forEach(([label, value]) => {
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(safeText(label), 25, yPos);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(safeText(value), 70, yPos);
    yPos += 7;
  });
  
  // Sekcja: Opakowanie
  yPos += 5;
  pdf.setFillColor(240, 240, 240);
  pdf.rect(20, yPos, pageWidth - 40, 8, 'F');
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('OPAKOWANIE', 22, yPos + 5.5);
  pdf.setFont('helvetica', 'normal');
  
  yPos += 12;
  
  pdf.setFontSize(10);
  const packagingData = [
    ['Typ:', result.box.name],
    ['Wymiary:', `${result.box.internalLength} x ${result.box.internalWidth} x ${result.box.internalHeight} mm`],
    ['Klasa przesylki:', result.shippingClass],
    ['Limit sztaplowania:', `${result.stackingLimit} ${result.stackingLimit === 1 ? 'warstwa' : 'warstwy'}`]
  ];
  
  packagingData.forEach(([label, value]) => {
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(safeText(label), 25, yPos);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(safeText(value), 70, yPos);
    yPos += 7;
  });
  
  // Sekcja: Materiały ochronne
  yPos += 5;
  pdf.setFillColor(240, 240, 240);
  pdf.rect(20, yPos, pageWidth - 40, 8, 'F');
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text(safeText('MATERIALY OCHRONNE'), 22, yPos + 5.5);
  pdf.setFont('helvetica', 'normal');
  
  yPos += 12;
  
  pdf.setFontSize(9);
  result.protection.forEach(p => {
    // Ikona typu materiału
    const icon = p.material.type === 'foam' ? '[P]' :
                 p.material.type === 'bubble' ? '[B]' :
                 p.material.type === 'corner' ? '[N]' :
                 p.material.type === 'separator' ? '[S]' : '[M]';
    
    pdf.setTextColor(255, 102, 0);
    pdf.text(icon, 25, yPos);
    
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'bold');
    pdf.text(safeText(p.material.name), 35, yPos);
    pdf.setFont('helvetica', 'normal');
    pdf.text(safeText(`${p.quantity} ${p.material.type === 'corner' ? 'szt.' : 'm²'}`), pageWidth - 50, yPos, { align: 'right' });
    
    yPos += 5;
    pdf.setTextColor(100, 100, 100);
    pdf.setFontSize(8);
    p.placement.forEach(place => {
      pdf.text(safeText(`• ${place}`), 40, yPos);
      yPos += 4;
    });
    pdf.setFontSize(9);
    yPos += 2;
  });
  
  // Sekcja: Współczynnik bezpieczeństwa
  yPos += 5;
  
  // Określ kolor na podstawie współczynnika
  const safetyColor = result.safetyFactor >= 8 ? { r: 16, g: 185, b: 129 } : // zielony
                      result.safetyFactor >= 6 ? { r: 245, g: 158, b: 11 } : // żółty
                      { r: 239, g: 68, b: 68 }; // czerwony
  
  pdf.setFillColor(safetyColor.r, safetyColor.g, safetyColor.b);
  pdf.rect(20, yPos, pageWidth - 40, 20, 'F');
  
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(11);
  pdf.text(safeText('WSPOLCZYNNIK BEZPIECZENSTWA'), pageWidth / 2, yPos + 8, { align: 'center' });
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`${result.safetyFactor}/10`, pageWidth / 2, yPos + 16, { align: 'center' });
  pdf.setFont('helvetica', 'normal');
  
  // Podsumowanie kosztów
  yPos += 25;
  
  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.5);
  pdf.line(20, yPos, pageWidth - 20, yPos);
  
  yPos += 8;
  pdf.setFontSize(10);
  pdf.setTextColor(0, 0, 0);
  
  const costBreakdown = [
    ['Opakowanie:', `${result.box.price.toFixed(2)} PLN`],
    ['Materialy ochronne:', `${result.protection.reduce((sum, p) => sum + (p.material.pricePerM2 * p.quantity), 0).toFixed(2)} PLN`]
  ];
  
  costBreakdown.forEach(([label, value]) => {
    pdf.setTextColor(100, 100, 100);
    pdf.text(safeText(label), pageWidth - 90, yPos);
    pdf.setTextColor(0, 0, 0);
    pdf.text(value, pageWidth - 25, yPos, { align: 'right' });
    yPos += 6;
  });
  
  pdf.setLineWidth(0.5);
  pdf.line(pageWidth - 90, yPos - 2, pageWidth - 20, yPos - 2);
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text(safeText('Koszt calkowity:'), pageWidth - 90, yPos + 5);
  pdf.setTextColor(255, 102, 0);
  pdf.text(safeText(`${result.totalCost.toFixed(2)} PLN`), pageWidth - 25, yPos + 5, { align: 'right' });
  
  // Stopka strony 1
  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text('PlexiSystem CRM', 20, pageHeight - 15);
  pdf.text(safeText('© 2025 PlexiSystem'), 20, pageHeight - 10);
  pdf.text(safeText(`Wygenerowano: ${new Date().toLocaleString('pl-PL')}`), pageWidth - 20, pageHeight - 15, { align: 'right' });
  pdf.text('Strona 1 z 2', pageWidth - 20, pageHeight - 10, { align: 'right' });
  
  // ============= STRONA 2: INSTRUKCJA KROK PO KROKU =============
  pdf.addPage();
  
  // Nagłówek strony 2
  pdf.setFontSize(16);
  pdf.setTextColor(0, 0, 0);
  pdf.text(safeText('INSTRUKCJA KROK PO KROKU'), pageWidth / 2, 25, { align: 'center' });
  
  yPos = 40;
  
  // Iteruj przez instrukcje
  let stepNumber = 1;
  let currentSection = '';
  
  result.instructions.forEach(instruction => {
    // Sprawdź czy potrzebna nowa strona
    if (yPos > 260) {
      pdf.addPage();
      yPos = 25;
    }
    
    if (instruction === '') {
      yPos += 3;
    } else if (instruction.includes('PRZYGOTOWANIE OPAKOWANIA') || 
               instruction.includes('UKLADANIE WARSTW') ||
               instruction.includes('UMIESZCZENIE PRODUKTU') ||
               instruction.includes('ZAMKNIECIE OPAKOWANIA') ||
               instruction.includes('OZNAKOWANIE')) {
      // Nagłówek sekcji
      const sectionTitle = instruction.replace(/[📦🛡️📐🔒🏷️]/g, '').trim();
      
      // Tło dla nagłówka sekcji
      pdf.setFillColor(255, 102, 0);
      pdf.rect(20, yPos, 10, 8, 'F');
      
      pdf.setFillColor(245, 245, 245);
      pdf.rect(30, yPos, pageWidth - 50, 8, 'F');
      
      // Ikona sekcji
      let icon = '';
      if (instruction.includes('PRZYGOTOWANIE')) icon = '1';
      else if (instruction.includes('UKLADANIE')) icon = '2';
      else if (instruction.includes('UMIESZCZENIE')) icon = '3';
      else if (instruction.includes('ZAMKNIECIE')) icon = '4';
      else if (instruction.includes('OZNAKOWANIE')) icon = '5';
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text(icon, 25, yPos + 5.5, { align: 'center' });
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(11);
      pdf.text(safeText(sectionTitle), 35, yPos + 5.5);
      pdf.setFont('helvetica', 'normal');
      
      yPos += 12;
      currentSection = sectionTitle;
    } else if (instruction.match(/^\d+\./)) {
      // Krok numerowany
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      
      // Numer kroku w kółku
      pdf.setDrawColor(255, 102, 0);
      pdf.setLineWidth(1);
      pdf.circle(27, yPos - 2, 3);
      
      pdf.setFontSize(8);
      pdf.setTextColor(255, 102, 0);
      const stepNum = instruction.match(/^\d+/)?.[0] || '';
      pdf.text(stepNum, 27, yPos, { align: 'center' });
      
      // Treść kroku
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      const stepText = instruction.replace(/^\d+\.\s*/, '');
      const lines = pdf.splitTextToSize(safeText(stepText), pageWidth - 60);
      lines.forEach((line: string, index: number) => {
        pdf.text(line, 35, yPos + (index * 5));
      });
      yPos += lines.length * 5 + 3;
    } else if (instruction.startsWith('   ')) {
      // Podpunkt
      pdf.setFontSize(9);
      pdf.setTextColor(100, 100, 100);
      pdf.text(safeText(instruction.trim()), 40, yPos);
      yPos += 5;
    } else if (instruction.startsWith('•')) {
      // Punkt wypunktowany
      pdf.setFontSize(9);
      pdf.setTextColor(50, 50, 50);
      
      // Kropka
      pdf.setFillColor(255, 102, 0);
      pdf.circle(32, yPos - 1.5, 1, 'F');
      
      const text = instruction.replace('•', '').trim();
      const lines = pdf.splitTextToSize(safeText(text), pageWidth - 60);
      lines.forEach((line: string, index: number) => {
        pdf.text(line, 37, yPos + (index * 4.5));
      });
      yPos += lines.length * 4.5 + 2;
    } else {
      // Zwykły tekst
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      const lines = pdf.splitTextToSize(safeText(instruction), pageWidth - 40);
      lines.forEach((line: string) => {
        pdf.text(line, 25, yPos);
        yPos += 5;
      });
    }
  });
  
  // Ramka z ważnymi informacjami
  if (yPos < 220) {
    yPos = 220;
  } else {
    pdf.addPage();
    yPos = 30;
  }
  
  // Ważne informacje
  pdf.setFillColor(255, 243, 224); // Jasny pomarańczowy
  pdf.rect(20, yPos, pageWidth - 40, 35, 'F');
  pdf.setDrawColor(255, 102, 0);
  pdf.setLineWidth(1);
  pdf.rect(20, yPos, pageWidth - 40, 35);
  
  pdf.setTextColor(255, 102, 0);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('WAZNE INFORMACJE', 25, yPos + 7);
  pdf.setFont('helvetica', 'normal');
  
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(9);
  const importantInfo = [
    safeText(`• Czas przygotowania: ok. ${result.estimatedPreparationTime} minut`),
    safeText(`• Klasa przesylki: ${result.shippingClass}`),
    safeText(`• Max sztaplowanie: ${result.stackingLimit} ${result.stackingLimit === 1 ? 'warstwa' : 'warstwy'}`),
    safeText(`• Wspolczynnik bezpieczenstwa: ${result.safetyFactor}/10`)
  ];
  
  importantInfo.forEach((info, index) => {
    pdf.text(info, 25, yPos + 14 + (index * 5));
  });
  
  // Stopka strony 2
  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text('PlexiSystem CRM', 20, pageHeight - 15);
  pdf.text(safeText('© 2025 PlexiSystem'), 20, pageHeight - 10);
  pdf.text(safeText(`Wygenerowano: ${new Date().toLocaleString('pl-PL')}`), pageWidth - 20, pageHeight - 15, { align: 'right' });
  pdf.text('Strona 2 z 2', pageWidth - 20, pageHeight - 10, { align: 'right' });
  
  return pdf;
};